"use client";

// GM overview KPI grid — GET /api/admin/dashboard.
// The API returns `{ value, available, reason }` for metrics it cannot compute
// yet; those render as "—" with the reason on hover instead of a fake number.
// No period-over-period deltas are exposed by the contract, so none are shown.
//
// Loading is a *state of this grid*, not a separate tree. The same components
// render throughout: dials and bars mount at zero with their readings
// skeletoned, and when the payload lands only the values change. Because the
// SVG arcs, the needle and the bar divs are never unmounted, the `anim-*`
// transitions in globals.css carry them from zero up to the real figure. A
// separate loading tree would remount them and they would snap into place
// fully drawn instead.

import { adminApi, metric, type AdminDashboard, type Metric } from "../lib/api";
import { useApi } from "../lib/useApi";
import { pct } from "../lib/format";
import Icon, { type IconName } from "./Icon";
import CountUp from "./CountUp";
import GaugeChart from "./GaugeChart";
import MiniBarChart from "./MiniBarChart";
import ProgressRing from "./ProgressRing";
import { Skeleton } from "./Skeleton";
import { PanelError, Unavailable } from "./PanelState";

function CardShell({
  label,
  tint,
  children,
}: {
  label: string;
  tint: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className="neu lift tint p-5 sm:p-6 flex flex-col gap-3"
      style={{ "--tint-color": tint } as React.CSSProperties}
    >
      <span className="text-text-muted text-[13px] font-medium">{label}</span>
      <div className="flex-1 grid place-items-center">{children}</div>
    </div>
  );
}

/** Wraps a metric that the contract may report as unavailable. While loading
 *  there is nothing to judge yet, so it renders the dial at zero rather than
 *  the "—" it shows for a genuinely missing figure. */
function MetricCard({
  label,
  tint,
  value,
  loading,
  render,
}: {
  label: string;
  tint: string;
  value: Metric | undefined;
  loading: boolean;
  render: (v: number, loading: boolean) => React.ReactNode;
}) {
  const m = metric(value);
  return (
    <CardShell label={label} tint={tint}>
      {loading ? (
        render(0, true)
      ) : m.available && m.value !== null ? (
        render(m.value, false)
      ) : (
        <div className="flex flex-col items-center gap-1.5 text-center">
          <span className="font-display text-3xl text-text-faint">—</span>
          <p className="text-[11.5px] text-text-faint max-w-45">{m.reason ?? "Not reported"}</p>
        </div>
      )}
    </CardShell>
  );
}

/** Bar labels and colours are known before the data is, so the loading charts
 *  render their real axes with every bar at zero. Keeping the same labels in
 *  the same order as the loaded data means the bars grow in place. */
const SOLD_LOST_PLACEHOLDER = [
  { label: "Sold", value: 0, color: "var(--ok)" },
  { label: "Lost", value: 0, color: "var(--danger)" },
];

const PIPELINE_PLACEHOLDER = [
  { label: "Active", value: 0, color: "var(--accent)" },
  { label: "Follow-up", value: 0, color: "var(--secondary)" },
  { label: "Waiting", value: 0, color: "var(--warn)" },
  { label: "Escalated", value: 0, color: "var(--danger)" },
];

/** Bell Performance rows. Labels are static; only the figures wait on the API. */
const BELL_STAT_LABELS = [
  "Total bell events",
  "Avg bells / deal",
  "Avg bells / sold",
  "Avg bells / lost",
];

const QUICK_STAT_META: { label: string; icon: IconName; pastel: string }[] = [
  { label: "Active Deals", icon: "handshake", pastel: "var(--mint)" },
  { label: "Deals Closed Today", icon: "tag", pastel: "var(--butter)" },
  { label: "Pending Follow-ups", icon: "clock", pastel: "var(--peach)" },
  { label: "Customers Logged", icon: "lot", pastel: "var(--sky)" },
];

export default function HeroDashboard() {
  const { data, loading, error, reload } = useApi<AdminDashboard>(
    (signal) => adminApi.dashboard({}, signal),
    [],
    "Failed to load the dashboard.",
  );

  if (!loading && (error || !data))
    return (
      <div className="neu p-5 sm:p-6">
        <PanelError message={error ?? "Dashboard unavailable."} onRetry={reload} />
      </div>
    );

  const traffic = data?.traffic;
  const salesPerformance = data?.salesPerformance;
  const managerPerformance = data?.managerPerformance;
  const bellPerformance = data?.bellPerformance;
  const financial = data?.financial;
  const pipeline = data?.pipeline;

  const soldLost = salesPerformance
    ? [
        { label: "Sold", value: salesPerformance.totalDealsSold, color: "var(--ok)" },
        { label: "Lost", value: salesPerformance.totalDealsLost, color: "var(--danger)" },
      ]
    : SOLD_LOST_PLACEHOLDER;

  const negotiating = metric(pipeline?.negotiating);
  const pipelineBars = pipeline
    ? [
        { label: "Active", value: pipeline.active, color: "var(--accent)" },
        { label: "Follow-up", value: pipeline.followUp, color: "var(--secondary)" },
        { label: "Waiting", value: pipeline.waitingForManager, color: "var(--warn)" },
        { label: "Escalated", value: pipeline.escalated, color: "var(--danger)" },
        ...(negotiating.available && negotiating.value !== null
          ? [{ label: "Negotiating", value: negotiating.value, color: "var(--accent-soft)" }]
          : []),
      ]
    : PIPELINE_PLACEHOLDER;

  const bellValues = bellPerformance
    ? [
        bellPerformance.totalBellEvents,
        bellPerformance.averageBellsPerDeal,
        bellPerformance.averageBellsPerSoldDeal,
        bellPerformance.averageBellsPerLostDeal,
      ]
    : [];

  const quickStats = traffic
    ? [
        traffic.activeDeals,
        traffic.dealsClosedToday,
        traffic.pendingFollowUps,
        traffic.totalCustomersLogged,
      ]
    : [];

  const grossPerBell = metric(financial?.grossPerBell);

  return (
    <div className="flex flex-col gap-4 lg:gap-6">
      {/* Row 1 — hero visuals */}
      <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 lg:gap-6">
        <CardShell label="Conversion Rate" tint="var(--mint)">
          <ProgressRing
            value={salesPerformance?.conversionRate ?? 0}
            max={100}
            label="sold / total customers"
            color="var(--ok)"
            loading={loading}
          />
        </CardShell>

        <MetricCard
          label="Avg Response Time"
          tint="var(--sky)"
          value={managerPerformance?.averageResponseTimeMinutes}
          loading={loading}
          // Contract reports minutes; the gauge is scaled in seconds.
          render={(mins, isLoading) => (
            <GaugeChart
              value={mins * 60}
              max={300}
              label="of 300s limit"
              unit="s"
              color="var(--secondary)"
              loading={isLoading}
            />
          )}
        />

        <MetricCard
          label="Total Gross"
          tint="var(--butter)"
          value={financial?.totalGross}
          loading={loading}
          render={(v, isLoading) =>
            isLoading ? (
              <Skeleton className="h-9 w-32 rounded-lg" />
            ) : (
              <CountUp
                value={v}
                format="compactMoney"
                className="font-display text-3xl sm:text-[2.1rem] leading-none text-text tabular-nums"
              />
            )
          }
        />

        <MetricCard
          label="Engagement Rate"
          tint="var(--lavender)"
          value={managerPerformance?.engagementPercentage}
          loading={loading}
          render={(v, isLoading) => (
            <ProgressRing
              value={v}
              max={100}
              label="bells acknowledged"
              color="var(--accent)"
              loading={isLoading}
            />
          )}
        />
      </section>

      {/* Row 2 — distribution visuals */}
      <section className="grid grid-cols-1 xl:grid-cols-3 gap-4 lg:gap-6">
        <div className="neu p-5 sm:p-6">
          <h2 className="font-display text-lg text-text mb-4">Bell Performance</h2>
          <ul className="flex flex-col gap-2.5">
            {BELL_STAT_LABELS.map((label, i) => (
              <li key={label} className="flex items-center justify-between gap-3 text-[12.5px]">
                <span className="text-text-muted truncate">{label}</span>
                {loading ? (
                  <Skeleton className="h-3.5 w-10 rounded" />
                ) : (
                  <span className="text-text font-semibold tabular-nums">
                    {Number.isInteger(bellValues[i]) ? bellValues[i] : bellValues[i].toFixed(1)}
                  </span>
                )}
              </li>
            ))}
            <li className="flex items-center justify-between gap-3 text-[12.5px]">
              <span className="text-text-muted truncate">Gross / bell</span>
              {loading ? (
                <Skeleton className="h-3.5 w-10 rounded" />
              ) : (
                <span className="text-text font-semibold tabular-nums">
                  {grossPerBell.available ? (
                    `$${Math.round(grossPerBell.value ?? 0).toLocaleString("en-US")}`
                  ) : (
                    <Unavailable reason={grossPerBell.reason} />
                  )}
                </span>
              )}
            </li>
          </ul>
        </div>

        <div className="neu p-5 sm:p-6 flex flex-col">
          <h2 className="font-display text-lg text-text mb-4">Sold vs Lost</h2>
          <div className="flex-1 flex flex-col justify-center gap-4">
            <MiniBarChart data={soldLost} loading={loading} />
            {loading ? (
              <Skeleton className="h-3.5 w-40 rounded" />
            ) : (
              <p className="text-[12.5px] text-text-faint">
                <span className="font-semibold" style={{ color: "var(--ok)" }}>
                  {pct(salesPerformance?.conversionRate ?? 0)}
                </span>{" "}
                conversion this period
              </p>
            )}
          </div>
        </div>

        <div className="neu p-5 sm:p-6 flex flex-col">
          <h2 className="font-display text-lg text-text mb-4">Deal Pipeline</h2>
          <div className="flex-1 flex flex-col justify-end">
            <MiniBarChart data={pipelineBars} direction="vertical" height={120} loading={loading} />
          </div>
        </div>
      </section>

      {/* Row 3 — quick stat strip */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        {QUICK_STAT_META.map((s, i) => (
          <div
            key={s.label}
            className="neu tint p-4 sm:p-5 flex items-center gap-3.5"
            style={{ "--tint-color": s.pastel } as React.CSSProperties}
          >
            <span
              className="chip neu-sm grid place-items-center w-11 h-11 rounded-2xl shrink-0"
              style={{ color: "var(--accent)" }}
            >
              <Icon name={s.icon} size={19} />
            </span>
            <div className="min-w-0 flex flex-col gap-1.5">
              <p className="text-[11.5px] text-text-muted truncate">{s.label}</p>
              {loading ? (
                <Skeleton className="h-5 w-14 rounded" />
              ) : (
                <CountUp
                  value={quickStats[i]}
                  format="int"
                  className="font-display text-xl text-text leading-tight tabular-nums"
                />
              )}
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}
